import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { DocumentTypeEnum } from '@/common/types';
import { API_URL } from '@/dtos';

import { useGetS3FileName } from '../hooks';

const getDocumentFiles = (documentId: string) => api.get(API_URL.DOCUMENTS_FILES_BY_DOCUMENT_ID(documentId));

export const getBlobType = (url: string) => {
  const formatStr = url?.split('?')?.[0]?.split('.')?.pop();
  const type = formatStr ? formatStr.trim().toLowerCase() : null;
  if (type === DocumentTypeEnum.PDF) return { format: 'application/pdf', type };
  return { format: `image/${type}`, type };
};
export interface ClaimFile {
  blob: string;
  id: string;
  link: string;
  fileId: string;
  name?: string;
  documentId: string;
  type: DocumentTypeEnum;
}

export const getFileFromDocumentById = async (documentId: string, fileId: string) => {
  const data = await getDocumentFiles(documentId);
  const files = data?.data?.files;
  const fileById = files?.find((file) => toString(file?.id) === fileId);
  return fileById;
};

export const QUERY_KEY = 'documentReceipts';
export const useGetDocumentFilesQuery = (documentId: string, isLoading: boolean) => {
  const { isLoading: isReceiptLoading, data } = useQuery([QUERY_KEY, documentId, isLoading], () =>
    documentId ? getDocumentFiles(documentId) : null
  );

  const [listOfDocuments, setListOfDocuments] = useState<string[]>([]);
  const [isFilesLoading, setIsFilesLoading] = useState(true);
  const documentFiles = useMemo(() => data?.data?.files || [], [data]);
  const documentIdFromResponse = useMemo(() => data?.data?.document_id, [data]);

  const getFiles = useCallback(
    async () =>
      documentFiles.length
        ? Promise.all(documentFiles.map((file) => fetch(file?.s3_url)))
            .then(async (responses) =>
              Promise.all(
                responses.map(async (response, index) => {
                  const blob = await response.arrayBuffer();
                  return new Blob([blob], { type: getBlobType(documentFiles[index]?.s3_url).format });
                })
              )
            )
            .then((blobArr) => blobArr.map((blob) => URL.createObjectURL(blob)))
            .catch(() => [])
        : [],
    [documentFiles]
  );

  useEffect(() => {
    setIsFilesLoading(true);
    setListOfDocuments([]);
    getFiles().then((files) => {
      setListOfDocuments(files);
      if (!isReceiptLoading) {
        setIsFilesLoading(false);
      }
    });
  }, [getFiles, isReceiptLoading]);

  useEffect(() => {
    setIsFilesLoading(true);
    setListOfDocuments([]);
  }, [documentId]);
  const getFileName = useGetS3FileName();
  const files: ClaimFile[] = listOfDocuments.map(
    (blob, index): ClaimFile => ({
      blob,
      id: blob,
      name: getFileName(documentFiles[index]?.s3_url),
      link: documentFiles[index]?.s3_url,
      fileId: toString(documentFiles[index]?.id),
      documentId: toString(documentIdFromResponse),
      type: getBlobType(documentFiles[index]?.s3_url).type as DocumentTypeEnum,
    })
  );
  return {
    files: files,
    isFilesLoading,
  };
};

export default useGetDocumentFilesQuery;
